/**
 * Service Request List Component
 * 내 서비스 요청 목록
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { CheckCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { ServiceQuoteCard } from "@/components/service/ServiceQuoteCard";

interface ServiceRequest {
  id: number;
  category: string;
  title: string;
  description: string;
  address: string;
  status: string;
  accepted_business_id?: string | null;
  matched_businesses?: Array<{ id: string; name: string }>;
  quotes?: Array<{
    id: number;
    business_id: string;
    quote_amount: number;
    description: string | null;
    status: string;
  }>;
  created_at: string;
}

type ServiceQuoteStatus = "pending" | "accepted" | "rejected" | "expired";

export function ServiceRequestList() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedRequest, setSelectedRequest] = useState<number | null>(null);

  // Fetch user's service requests
  const { data: requestsData, isLoading } = useQuery<{ items: ServiceRequest[] }>({
    queryKey: ["/api/service-requests", "my"],
    queryFn: async () => {
      const res = await fetch("/api/service-requests?me=1");
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("로그인이 필요합니다");
        }
        throw new Error("요청 목록을 불러올 수 없습니다");
      }
      return res.json();
    },
    enabled: !!user,
  });

  const requests = requestsData?.items || [];

  const acceptQuoteMutation = useMutation({
    mutationFn: async ({
      requestId,
      quoteId,
    }: {
      requestId: number;
      quoteId: number;
    }) => {
      return apiRequest("PUT", `/api/service-requests/${requestId}/accept`, {
        quote_id: quoteId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/service-requests"],
      });
      toast({
        title: "견적이 수락되었습니다",
      });
      setSelectedRequest(null);
    },
    onError: (error: Error) => {
      toast({
        title: "수락 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "open":
        return <Badge variant="outline">요청 중</Badge>;
      case "matched":
        return <Badge className="bg-blue-600">매칭됨</Badge>;
      case "quoted":
        return <Badge className="bg-yellow-600">견적 도착</Badge>;
      case "accepted":
        return <Badge className="bg-green-600">수락됨</Badge>;
      case "completed":
        return <Badge className="bg-green-700">완료</Badge>;
      case "cancelled":
        return <Badge variant="destructive">취소됨</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      plumbing: "배관",
      electrical: "전기",
      cleaning: "청소",
      moving: "이사",
      food: "음식",
    };
    return labels[category] || category;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        등록한 서비스 요청이 없습니다
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {requests.map((request) => (
        <Card key={request.id}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold">{request.title}</span>
                  <Badge variant="outline">
                    {getCategoryLabel(request.category)}
                  </Badge>
                  {getStatusBadge(request.status)}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {request.description}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(request.created_at), {
                    addSuffix: true,
                    locale: ko,
                  })}
                </p>
              </div>
            </div>

            {/* Quotes */}
            {request.quotes && request.quotes.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="text-sm font-medium">받은 견적 ({request.quotes.length}개):</p>
                {request.quotes.map((quote) => (
                  <ServiceQuoteCard
                    key={quote.id}
                    quote={{
                      ...quote,
                      business_name: undefined, // TODO: Fetch business name from API
                      status: quote.status as ServiceQuoteStatus,
                    }}
                    requestId={request.id}
                    onAccept={(quoteId: number) => {
                      acceptQuoteMutation.mutate({
                        requestId: request.id,
                        quoteId,
                      });
                    }}
                    isAccepted={request.accepted_business_id === quote.business_id}
                    canAccept={request.status === "quoted" && quote.status === "pending"}
                  />
                ))}
              </div>
            )}

            {/* Matched Businesses */}
            {request.matched_businesses &&
              request.matched_businesses.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">
                    매칭된 업체 ({request.matched_businesses.length}개):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {request.matched_businesses.map((business) => (
                      <Badge key={business.id} variant="outline">
                        {business.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
